import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  SmsConsentCheckboxes,
  type SmsConsentFormData,
  smsConsentDefaultValues,
  smsConsentSchema,
} from '@/components/SmsConsentCheckboxes';
import { Button } from '@/components/ui/button.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { prettyTypeName } from '@/types/insurance.type.ts';

const formId = 'discovery-commercial-note-form';

interface Props {
  onAccept: () => void;
}

export function DiscoveryCommercialNote({ onAccept }: Props) {
  const { insuranceBuilder } = useSessionStore();

  const form = useForm<SmsConsentFormData>({
    resolver: zodResolver(smsConsentSchema),
    defaultValues: smsConsentDefaultValues,
  });

  const carrierNames = useMemo(
    () => [...insuranceBuilder.carrierTypes].map(prettyTypeName),
    [insuranceBuilder.carrierTypes]
  );

  return (
    <form
      id={formId}
      onSubmit={form.handleSubmit(() => onAccept())}
      className="space-y-6"
      role="status"
      aria-live="polite"
      aria-labelledby="eligibility-result"
    >
      <div className="text-center">
        <h2 id="eligibility-result" className="mb-4 font-serif text-2xl">
          Note:
        </h2>
        <p className="mb-6 font-serif text-xl">
          We'll determine your eligibility based on your {carrierNames.join(' & ')} benefits.
        </p>
        <p className="text-base text-gray-700">
          We're currently servicing Medicaid & Medicare plans only.
        </p>
      </div>

      <SmsConsentCheckboxes control={form.control} formId={formId} />

      <Button type="submit" className="w-full">
        I understand
      </Button>
    </form>
  );
}
